import React from "react";
import config from "./config";
import isEqual from "lodash/isEqual";
import moment from "moment-timezone";
import replace from "string-replace-to-array";
import * as ColorManipulator from "material-ui/utils/colorManipulator";
import qs from "qs";

import { useRouterHistory } from "react-router";
import createBrowserHistory from "history/lib/createBrowserHistory";
import createHashHistory from "history/lib/createHashHistory";
// const createAppHistory = useRouterHistory(process.env.NODE_ENV === 'production' ?  createBrowserHistory : createHashHistory);
const createAppHistory = useRouterHistory(createBrowserHistory);
export const history = createAppHistory({
  parseQueryString: qs.parse,
  stringifyQuery: qs.stringify
});

/* intersperse: Return an array with the separator interspersed between
 * each element of the input array.
 *
 * > _([1,2,3]).intersperse(0)
 * [1,0,2,0,3]
 */
export function intersperse(arr, sep) {
  if (arr.length === 0) {
    return [];
  }

  return arr.slice(1).reduce(
    function (xs, x, i) {
      return xs.concat([sep, x]);
    },
    [arr[0]]
  );
}

/* Format a single price in cents to $xx.yy format. If an array in the form
 * [[price1, quantity1], [price2, quantity2], ...] is passed, return a string
 * in the format "$xx.yy (xQ), $yy.zz (xQ)" where Q is the quantity of each price.
 * Used for showing multiple prices.
 */
export function formatPrice(p, short = false, implicit = false) {
  let format = p => {
    // Zero-decimal currency
    if (["krw", "clp", "jpy"].includes(TENANT.currency)) {
      p *= 100;
      short = true;
    }

    let formatted = (p / 100).toLocaleString(TENANT.locale || "en-US", {
      style: "currency",
      currency: (TENANT.currency || "usd").toUpperCase(),
      minimumFractionDigits: 2
    });

    if (short) {
      // Strip trailing .00 from price, leaving just $XYZ
      formatted = formatted.replace(/\.00$/, "");
    }

    if (implicit || TENANT.hide_currency_country_prefix) {
      // strip preceding CA
      formatted = formatted.replace(/^(CA|MX)/, "");
    }

    return formatted;
  };

  if (Array.isArray(p)) {
    return p
      .map(([price, quantity]) => `${format(price)} (x${quantity})`)
      .join(", ");
  } else {
    return format(p);
  }
}

export function formatPercent(n = 0, { locale } = {}) {
  locale = locale || TENANT.locale || "en-US";
  return (n || 0).toLocaleString(locale, {
    style: "percent",
    maximumFractionDigits: 4
  });
}

export function getPriceForItemAtQuantity(item, quantity) {
  // Apply volume price - find the highest minimum quantity that the current order matches
  if (
    item &&
    item.volume_price_enabled &&
    !(item.sale_price && quantity === 1)
  ) {
    let discount = { minimum_quantity: 0, price: item.price };
    item.volume_price.forEach(potentialDiscount => {
      if (
        quantity >= potentialDiscount.minimum_quantity &&
        potentialDiscount.minimum_quantity > discount.minimum_quantity
      ) {
        discount = potentialDiscount;
      }
    });
    return discount.price;
  } else {
    return item.sale_price || item.price;
  }
}

export function applyCoupon(
  subitem,
  price,
  item,
  coupon,
  appSettings,
  _applyCouponState = {}
) {
  let originalPrice = price,
    isVolumeDiscounted = false,
    rawTotal,
    fee;

  // If no price (e.g. an empty inline donation)
  if (!price) {
    price = 0;
  }

  if (!coupon) {
    coupon = {};
  }

  // Apply volume price - find the highest minimum quantity that the current order matches
  // Note: For quantity=1 when a sale price is enabled, we use the sale price instead.
  if (
    item &&
    item.volume_price_enabled &&
    !(item.sale_price && subitem.quantity === 1)
  ) {
    let discount = { minimum_quantity: 0, price: price };
    item.volume_price.forEach(potentialDiscount => {
      if (
        subitem.quantity >= potentialDiscount.minimum_quantity &&
        potentialDiscount.minimum_quantity > discount.minimum_quantity
      ) {
        discount = potentialDiscount;
      }
    });
    price = discount.price;
    isVolumeDiscounted = true;
  }

  // Apply sale price
  if (item && item.sale_price && price === item.sale_price) {
    originalPrice = item.price;
  }

  // Apply percent off / set_price discount, including restricting by quantity
  let percentToDiscount = 1,
    discountedPrice = coupon.set_price
      ? coupon.set_price
      : price - price * ((coupon.percent_off || 0) / 100);
  if (coupon.cents_off_item) {
    discountedPrice -= coupon.cents_off_item;
  }
  discountedPrice = Math.max(0, discountedPrice);
  if (coupon.max_quantity && subitem.quantity > coupon.max_quantity) {
    percentToDiscount = coupon.max_quantity / subitem.quantity;
    price = [
      [discountedPrice, coupon.max_quantity],
      [price, subitem.quantity - coupon.max_quantity]
    ];
  } else {
    price = discountedPrice;
  }

  rawTotal = Array.isArray(price)
    ? price.reduce((total, p) => total + p[0] * p[1], 0)
    : price * (subitem.quantity === 0 ? 0 : subitem.quantity || 1);

  // Calculate cents_off
  if (coupon.cents_off) {
    rawTotal = Math.max(0, rawTotal - coupon.cents_off);
  }

  // Calculate fee - if the item is recurring, use the original fee on the item to compute,
  // since the fee is charged as a flat rate sort of fee.
  if (item && item.recurrence_fee_percent && !subitem.inline_donation) {
    fee =
      rawTotal === 0
        ? 0
        : subitem.quantity *
          calculateFeeForPrice(
            originalPrice,
            1,
            appSettings,
            subitem,
            _applyCouponState
          );
  } else {
    fee = calculateFeeForPrice(
      rawTotal,
      subitem.quantity,
      appSettings,
      subitem,
      _applyCouponState
    );
  }

  // Don't show an "original price" because the cents_off discount applies to the total
  if (
    (!coupon.percent_off && !coupon.cents_off_item && !isVolumeDiscounted) ||
    originalPrice === price
  ) {
    originalPrice = undefined;
  }

  // Calculate tax -- FEE NOT INCLUDED AS PART OF IT
  let tax = 0;
  if (appSettings.tax_percent && subitem.quantity) {
    let type = subitem.pass_bucket
      ? "pass"
      : subitem.ticket_bucket
      ? "ticket"
      : subitem.item_bucket
      ? "item"
      : "";
    if (appSettings.tax_for.indexOf(type) !== -1) {
      // Inject on a per-item basis to match ledger style rounding.
      tax =
        subitem.quantity *
        Math.round(appSettings.tax_percent * (rawTotal / subitem.quantity));
      rawTotal += tax;
    }
  }

  let total = rawTotal + fee;

  return { originalPrice, price, fee, tax, total };
}

export function calculateApplicationFeeForPrice(
  amount,
  quantity = 1,
  appSettings = {},
  subitem = {}
) {
  // No Eventive fee on donations
  if (subitem && subitem.donation) {
    return 0;
  }

  return (
    amount *
      (appSettings.fee_percent
        ? appSettings.fee_percent * 0.01 + 0.001
        : TENANT.fee.platform_percent[TENANT.currency]) +
    TENANT.fee.platform_flat_rate[TENANT.currency] * quantity +
    TENANT.fee.stripe_flat_rate[TENANT.currency] * (quantity - 1)
  );
}

export function calculateFeeForPrice(
  price,
  quantity = 1,
  appSettings = {},
  subitem = {},
  _applyCouponState = {}
) {
  if (!price) {
    return 0;
  }

  if (
    (subitem.donation && appSettings.absorb_donation_fees) ||
    subitem.inline_donation ||
    subitem.item_bucket ||
    (appSettings.absorb_ticket_fees && subitem.ticket_bucket) ||
    (appSettings.absorb_pass_fees && subitem.pass_bucket) ||
    (quantity > 0 &&
      appSettings.absorb_ticket_fees_under &&
      appSettings.absorb_ticket_fees_under > price / quantity &&
      subitem.ticket_bucket) ||
    (quantity > 0 &&
      appSettings.absorb_pass_fees_under &&
      appSettings.absorb_pass_fees_under > price / quantity &&
      subitem.pass_bucket)
  ) {
    return 0;
  }

  // Calculate fee by summing for each i=0...quantity, to match Ledger-style rounding.
  const perItemPrice = price / quantity;
  let calculated = 0;
  for (let i = 0; i < quantity; i++) {
    let piece;
    if (appSettings.pricing_plan === "sundance") {
      piece =
        perItemPrice * 0.035 +
        (subitem.ticket_bucket && perItemPrice > 0 ? 35 : 0);
    } else if (appSettings.pricing_plan === "cphdox" && subitem.ticket_bucket) {
      piece =
        (subitem.ticket_bucket && perItemPrice > 0 ? 1000 : 0);
    } else {
      piece = Math.round(
        (perItemPrice +
          calculateApplicationFeeForPrice(
            perItemPrice,
            1,
            appSettings,
            subitem
          ) +
          TENANT.fee.stripe_flat_rate[TENANT.currency]) /
          (1 - TENANT.fee.stripe_percent[TENANT.currency]) -
          perItemPrice
      );
    }

    calculated += Math.round(
      (piece * (100 - appSettings.absorb_fee_percent)) / 100
    );
  }

  if (appSettings.fee_cap) {
    let feeCapped = Math.min(appSettings.fee_cap, calculated);

    // If we're applying the fee cap across multiple subitems, ensure
    // we don't exceed the cap across all the subitems together.
    if (_applyCouponState) {
      let alreadyUsedFeeCap = _applyCouponState.feeCapUsed || 0;
      let remainingFeeCap = appSettings.fee_cap - alreadyUsedFeeCap;
      if (remainingFeeCap < feeCapped) {
        _applyCouponState.feeCapUsed = appSettings.fee_cap;
        return remainingFeeCap;
      } else {
        _applyCouponState.feeCapUsed = alreadyUsedFeeCap + feeCapped;
        return feeCapped;
      }
    }

    return feeCapped;
  } else {
    return calculated;
  }
}

// Given a tag object { name: xx, color: xy (optional) }, select a color for the tag
export function getColorForTag(content, isDisabled, customFade, tenant) {
  let tagColors = tenant.tag_colors,
    tagKeyColors = tenant.custom_tag_colors,
    color,
    chars,
    uid;

  if (typeof content === "string") {
    content = { name: content };
  }

  if (content.color) {
    color = content.color;
  } else if (tagKeyColors[content.name]) {
    color = tagKeyColors[content.name];
  } else {
    chars = (content.name || "Unnamed Event").split("");
    uid = chars
      .slice(0, 1)
      .concat(chars.slice(chars.length - 1))
      .map(c => c.codePointAt(0))
      .reduce((prev, next) => prev + next, 0);
    color = tagColors[uid % tagColors.length];
  }

  if (isDisabled) {
    color = ColorManipulator.fade(color, customFade || 0.6);
  }

  return color;
}

export function shouldComponentUpdateDeep(nextProps, nextState, nextContext) {
  return (
    !isEqual(nextProps, this.props) ||
    !isEqual(nextState, this.state) ||
    !isEqual(nextContext, this.context)
  );
}

export const IS_MOBILE = /Mobi/.test(navigator.userAgent);
export function hasMobileUA() {
  return IS_MOBILE;
}

const _capitalize = s => s[0].toUpperCase() + s.slice(1);
export function capitalize(s, titleCase = false) {
  if (!s) {
    return "";
  }
  if (titleCase) {
    return s
      .toLowerCase()
      .replace(/(?:^|[\s-/])\w/g, match => match.toUpperCase());
  }
  return _capitalize(s);
}

export function formatSubscriptionStatus(subscription) {
  if (subscription.status === "active") {
    var term = subscription.should_autorenew ? "Renews" : "Expires";
    return `${term} ${moment(subscription.renewal_date).format("LL")}`;
  } else {
    return `Expired ${moment(subscription.expired_date).format("LL")}`;
  }
}

/**
 * Replaces insertables in the form {{inserable_name|arg1|arg2|arg3...}} with whatever
 * the associated insertables[insertable_name] function returns when called with the arguments.
 * Currently only supports up to 3 arguments.
 */
export function formatInsertables(text, insertables) {
  // string-replace-to-array does not support an empty string
  if (!text) {
    return "";
  }

  let replaced = text;

  Object.keys(insertables).forEach(k => {
    replaced = replace(
      replaced,
      new RegExp(
        "{{" + k + "(?:\\|([^|]*?))?(?:\\|([^|]*?))?(?:\\|([^|]*?))?}}",
        "g"
      ),
      function () {
        return insertables[k].apply(
          null,
          Array.prototype.slice.call(arguments, 1)
        );
      }
    );
  });

  return replaced;
}

let iframeInfoCallbacks = [],
  iframeInfoPending = false;

function onIframeInfo(info) {
  iframeInfoCallbacks.forEach(cb => cb(info));
  iframeInfoCallbacks = [];
  iframeInfoPending = false;
}

export function getIframePageInfoOnce(cb) {
  if (!window.parentIFrame) {
    return;
  }

  iframeInfoCallbacks.push(cb);

  if (!iframeInfoPending) {
    window.parentIFrame.getPageInfo(onIframeInfo);
    iframeInfoPending = true;
  }
}

const HIGHLIGHT_START = '<span style="background-color: yellow;">';
const HIGHLIGHT_END = "</span>";
export function highlightTextAtIndices(text, indices) {
  let lastEnd = 0,
    newText = "";
  indices.forEach(([start, end]) => {
    newText +=
      text.substring(lastEnd, start) +
      HIGHLIGHT_START +
      text.substring(start, end + 1) +
      HIGHLIGHT_END;
    lastEnd = end + 1;
  });
  newText += text.substring(lastEnd);
  return newText;
}

export function makeFilmDetailString(details) {
  let detailString = [
    details.runtime &&
      `${details.runtime}${isNaN(details.runtime) ? "" : " minutes"}`,
    details.country,
    details.year
  ]
    .filter(x => !!x)
    .join(" | ");
  return detailString;
}

export function dashify(str) {
  return str
    .trim()
    .replace(/_/g, "-")
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/\W/g, m => (/[À-ž]/.test(m) ? m : "-"))
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, m => "-")
    .toLowerCase();
}

export function slugify(getItem, idKey) {
  if (!TENANT.urls_use_slugs) {
    return WrappedComponent => WrappedComponent;
  }

  return function (WrappedComponent) {
    return class extends React.Component {
      static contextTypes = {
        router: React.PropTypes.object
      };

      componentDidMount() {
        this.componentWillReceiveProps(this.props);
      }

      componentWillReceiveProps(props) {
        let id = props.params[idKey],
          item = getItem(id);

        if (item) {
          let slug = dashify(item.name);
          if (props.params.splat !== slug) {
            let { location } = props;
            location.pathname = location.pathname.replace(
              new RegExp(
                `${props.params.splat ? `${props.params.splat}-` : ""}${id}`
              ),
              `${slug}-${id}`
            );
            location.state = location.state || {};
            if (location.action === "PUSH") {
              location.state.replacedFromPush = true;
            } else {
              delete location.state.replacedFromPush;
            }
            this.context.router.replace(location);
          }
        }
      }

      render() {
        return <WrappedComponent {...this.props} />;
      }
    };
  };
}

export function checkDevice() {
  const isAndroid = navigator.userAgent.toLowerCase().indexOf("android") > -1;
  const isApple = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return {
    isAndroid: isAndroid,
    isApple: isApple,
    isMobile: isAndroid || isApple
  };
}

export function isApp() {
  return (
    navigator.userAgent.toLowerCase().indexOf("gonative") > -1 &&
    /iPad|iPhone|iPod/.test(navigator.userAgent)
  );
}



// WEBPACK FOOTER //
// ./src/app/util.js